// @tg-seq: 10673
// @tg-api-binding: 10224-1,10672-1,10672-2,10673-1
/**
 * Event Submission Form (Dynamic, Field-Type-Driven)
 *
 * Purpose: Standalone mobile-first workflow -- pick a Lifecycle Type, pick
 * one of its Event Types, then fill out whichever Submission Questions are
 * configured for that Event Type (authored in the Lifecycle Event Type
 * Library admin tab, scr_tgc_10672_lifecycle_event_type.tsx). Each question's
 * input control is chosen dynamically from its Field_Type
 * (text/number/boolean/date/single_choice/multi_choice/photo), so adding a
 * new question in the admin tab immediately changes what this form renders
 * -- no code change needed here.
 *
 * On Submit, the full set of answers is sent as ONE raw JSON object
 * (Submission_Payload_Json). Parsing individual answers into normalized DB
 * columns (using each question's optional Target_Schema_Name/Target_Table_Name/
 * Target_Field_Name mapping) is a deliberate, separate, later concern.
 *
 * 'dedicated' storage_mode questions ALSO get their single answer value
 * dispatched (immediately after the main submit resolves a new
 * Universal_Event_UUID) to the standalone Universal Answer Dispatcher router
 * (/api/universal-answer-dispatch, TGSYS.sp_Universal_Answer_Dispatcher_Controller)
 * -- always @manage=1 (Add), since this form only ever creates brand-new
 * Active_Universal_Event rows (no existing-submission edit flow exists here).
 * The dispatcher resolves each question's mapped, already-governed target
 * controller via Target_TSX_Seq_Num/Target_Router_Ordinal and never performs
 * raw dynamic table/column DML itself.
 *
 * Reuses existing endpoints rather than duplicating them:
 *   - 10224/1 (@manage=18): Lifecycle Type picker
 *   - 10672/1 (@manage=5): Event Type picker, scoped by Lifecycle_Type_ID
 *   - 10672/2 (@manage=5): Submission Questions for the selected Event Type
 *     (includes Field_Type + Options_Json + Storage_Mode + Target_* dispatch mapping)
 *   - 10673/1 (@manage=2): Submit the completed form (writes to the REAL
 *     shared platform table, not a bespoke per-feature one)
 *   - 10673/1/upload: Photo capture for 'photo' field_type questions
 *   - /api/universal-answer-dispatch (@manage=1): dispatch 'dedicated' answers
 *     to their mapped target controllers
 *
 * Table: SSCCO.Active_Universal_Event
 * Controller: SSCCO.sp_Active_Universal_Event_Controller
 * Created: 2026-10-15
 */

// === AUTO-GENERATED START: API_ENDPOINTS ===
/**
 * API Endpoint Registry - Component 10673
 * Generated from Router_Registry (TSX_Seq_Num = 10673)
 * Auto-synced on build - DO NOT EDIT MANUALLY
 *
 * Router 1: tsx_10673_1.py (TSX_COMPONENT)
 *   Controller: SSCCO.sp_Active_Universal_Event_Controller
 *   Endpoint: /api/tsx/10673/1
 *   Usage: Event Submission create/list/stats -- writes to the real shared SSCCO.Active_Universal_Event platform table (raw JSON Q&A payload)
 *
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const API_ENDPOINTS = {
    MAIN: '/api/tsx/10673/1',
};
// === AUTO-GENERATED END: API_ENDPOINTS ===

import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    Chip,
    CircularProgress,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormHelperText,
    IconButton,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    SelectChangeEvent,
    Snackbar,
    Stack,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import axios from 'axios';
import React, { useCallback, useEffect, useState } from 'react';
import { apiRequest, apiRequestBySeq } from '../../utils/apiService';
import { getTsxFileName } from '../../utils/componentIdentity';
import { resolveRouterEndpoint } from '../../utils/routerResolver';
import { SecureStorage } from '../../utils/SecureStorage';
import { useAuth } from '../../utils/useAuth';

// =============================================
// Types
// =============================================

interface LifecycleTypeOption {
    lifecycle_type_id: number;
    type_code: string;
    type_name: string;
    is_composite_plan?: number | boolean;
    enable_event_submission?: number | boolean;
}

interface EventType {
    event_type_id: number;
    lifecycle_type_id: number;
    event_type: string;
    isactive: number;
}

interface QuestionOption {
    option_id: number;
    option_label: string;
    option_value: string;
    sort_order: number;
}

interface SubmissionQuestion {
    submission_question_id: number;
    event_type_id: number;
    submission_question: string;
    field_type: string;
    isrequired: number;
    sort_order: number;
    isactive: number;
    options_json?: string | null;
    fk_allow_multiple?: number;
    allow_multiple_answers?: number;
    accepted_file_types?: string;
    max_file_size_mb?: number;
    storage_mode?: string;
    target_tsx_seq_num?: number;
    target_router_ordinal?: number;
    target_field_name?: string;
    target_link_field_name?: string;
    target_pk_field_name?: string;
    target_manage_add?: number;
    target_manage_edit?: number;
    target_manage_delete?: number;
    target_pass_organization_uuid?: number;
    target_pass_orgrole_id?: number;
    target_pass_pfuser_uuid?: number;
}

interface ApiEnvelope<T> {
    success: boolean;
    data: T;
    count: number;
}

type AnswerValue = string | number | boolean | string[] | null;

interface EventSubmissionFormProps {
    // When provided, pre-selects this Lifecycle Type once the picker loads and
    // locks the Lifecycle Type dropdown (disabled) so a caller that's opening
    // this form for a specific purpose -- e.g. the Phone Tool's "ERS REQUEST"
    // Quick Action presetting Lifecycle_Type_ID 15 / ERS_Roadside -- can't have
    // that context accidentally changed mid-flow. Omit to use the form's normal
    // free-choice Lifecycle Type picker (e.g. the Welcome page's "Submit an
    // Event" dialog).
    initialLifecycleTypeId?: number;
}

// 'text' / 'number' / 'date' / 'photo' / 'file' questions flagged
// Allow_Multiple_Answers render as a repeatable list of inputs/uploads (e.g.
// "Attach photos of the damage" may have several) -- all answers fold into
// ONE array value under this question's key in the single submission JSON
// payload, rather than requiring a separate submission record per answer.
const isRepeatableAnswer = (q: SubmissionQuestion) =>
    !!q.allow_multiple_answers &&
    (q.field_type === 'text' ||
        q.field_type === 'number' ||
        q.field_type === 'date' ||
        q.field_type === 'photo' ||
        q.field_type === 'file');

// =============================================
// Main Component
// =============================================

const Self: React.FC<EventSubmissionFormProps> = ({ initialLifecycleTypeId }) => {
    const { pfUser, activeOrganizationUUID, activeOrgRoleId, userOrganizations } = useAuth();
    // Header org switcher stores the literal string 'ALL' (not null) when
    // "All Organizations" is selected -- Owner_Organization_uuid is a NOT
    // NULL column on SSCCO.Active_Universal_Event. When the header has a
    // specific organization active, that's the preset/locked value for this
    // submission (same preset-lock pattern as initialLifecycleTypeId below).
    // When the header is "All Organizations" there's nothing to preset, so
    // the Organization picker below renders inline and the user chooses from
    // their own org associations (selectedOrgUuid) before they can submit.
    const isAllOrgsActive = !activeOrganizationUUID || activeOrganizationUUID === 'ALL';

    // ---- State: Pickers ----
    const [selectedOrgUuid, setSelectedOrgUuid] = useState<string>('');
    const normalizedOrgUUID = isAllOrgsActive ? selectedOrgUuid || null : activeOrganizationUUID;
    const [lifecycleTypes, setLifecycleTypes] = useState<LifecycleTypeOption[]>([]);
    const [selectedLifecycleTypeId, setSelectedLifecycleTypeId] = useState<number | ''>('');
    const [eventTypes, setEventTypes] = useState<EventType[]>([]);
    const [selectedEventType, setSelectedEventType] = useState<EventType | null>(null);

    // ---- State: Dynamic Questions / Answers ----
    const [questions, setQuestions] = useState<SubmissionQuestion[]>([]);
    const [answers, setAnswers] = useState<Record<number, AnswerValue>>({});
    const [uploadingQuestionId, setUploadingQuestionId] = useState<number | null>(null);

    // ---- State: Loading / Submission ----
    const [loading, setLoading] = useState(false);
    const [questionsLoading, setQuestionsLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submittedUuid, setSubmittedUuid] = useState<string | null>(null);
    const [error, setError] = useState('');

    // ---- State: Snackbar ----
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
        severity: 'success' | 'error' | 'info' | 'warning';
    }>({ open: false, message: '', severity: 'info' });

    // ════════════════════════════════════════
    // Data Loading
    // ════════════════════════════════════════

    const loadLifecycleTypes = useCallback(async () => {
        try {
            setLoading(true);
            const response = await apiRequestBySeq<ApiEnvelope<LifecycleTypeOption[]>>(10224, 1, {
                manage: 18,
            });
            const eligible = (response?.data || []).filter(
                (t) =>
                    (!t.is_composite_plan || t.is_composite_plan === 0) &&
                    !!t.enable_event_submission,
            );
            setLifecycleTypes(eligible);
        } catch (err: any) {
            console.error('Error loading lifecycle types:', err);
            setError('Failed to load lifecycle types');
        } finally {
            setLoading(false);
        }
    }, []);

    const loadEventTypes = useCallback(async (lifecycleTypeId: number) => {
        try {
            setLoading(true);
            const response = await apiRequestBySeq<ApiEnvelope<EventType[]>>(10672, 1, {
                manage: 5,
                lifecycle_type_id: lifecycleTypeId,
            });
            setEventTypes((response?.data || []).filter((et) => et.isactive));
        } catch (err: any) {
            console.error('Error loading event types:', err);
            setError('Failed to load event types');
        } finally {
            setLoading(false);
        }
    }, []);

    const loadQuestions = useCallback(async (eventTypeId: number) => {
        try {
            setQuestionsLoading(true);
            const response = await apiRequestBySeq<ApiEnvelope<SubmissionQuestion[]>>(10672, 2, {
                manage: 5,
                event_type_id: eventTypeId,
                isactive: 1,
            });
            const active = response?.data || [];
            setQuestions(active);

            // Seed default answers (multi_choice/repeatable -> empty array, boolean -> false, others -> empty string)
            const defaults: Record<number, AnswerValue> = {};
            active.forEach((q) => {
                const isMultiLike =
                    q.field_type === 'multi_choice' ||
                    (q.field_type === 'fk_lookup' && !!q.fk_allow_multiple) ||
                    isRepeatableAnswer(q);
                defaults[q.submission_question_id] = isMultiLike
                    ? []
                    : q.field_type === 'boolean'
                      ? false
                      : '';
            });
            setAnswers(defaults);
        } catch (err: any) {
            console.error('Error loading submission questions:', err);
            setError('Failed to load submission questions');
        } finally {
            setQuestionsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadLifecycleTypes();
    }, [loadLifecycleTypes]);

    // Preset from caller (e.g. Phone Tool ERS quick action) once the eligible
    // Lifecycle Types have loaded -- only applies if the preset ID is actually
    // in the loaded/filtered list (enable_event_submission=1, non-composite).
    useEffect(() => {
        if (
            initialLifecycleTypeId !== undefined &&
            selectedLifecycleTypeId === '' &&
            lifecycleTypes.some((t) => t.lifecycle_type_id === initialLifecycleTypeId)
        ) {
            setSelectedLifecycleTypeId(initialLifecycleTypeId);
        }
    }, [initialLifecycleTypeId, lifecycleTypes, selectedLifecycleTypeId]);

    useEffect(() => {
        if (selectedLifecycleTypeId !== '') {
            loadEventTypes(selectedLifecycleTypeId as number);
        } else {
            setEventTypes([]);
        }
        setSelectedEventType(null);
        setQuestions([]);
        setAnswers({});
        setSubmittedUuid(null);
    }, [selectedLifecycleTypeId, loadEventTypes]);

    // ════════════════════════════════════════
    // Handlers
    // ════════════════════════════════════════

    const handleOrgChange = (e: SelectChangeEvent<string>) => {
        setSelectedOrgUuid(e.target.value);
    };

    const handleLifecycleTypeChange = (e: SelectChangeEvent<number | string>) => {
        const value = e.target.value;
        setSelectedLifecycleTypeId(value === '' ? '' : Number(value));
    };

    const handleEventTypeChange = (e: SelectChangeEvent<number | string>) => {
        const value = e.target.value;
        if (value === '') {
            setSelectedEventType(null);
            setQuestions([]);
            setAnswers({});
            return;
        }
        const eventType = eventTypes.find((et) => et.event_type_id === Number(value)) || null;
        setSelectedEventType(eventType);
        setSubmittedUuid(null);
        if (eventType) loadQuestions(eventType.event_type_id);
    };

    const setAnswer = (questionId: number, value: AnswerValue) => {
        setAnswers((prev) => ({ ...prev, [questionId]: value }));
    };

    const handleMultiChoiceToggle = (questionId: number, optionValue: string, checked: boolean) => {
        setAnswers((prev) => {
            const current = Array.isArray(prev[questionId]) ? (prev[questionId] as string[]) : [];
            const next = checked
                ? [...current, optionValue]
                : current.filter((v) => v !== optionValue);
            return { ...prev, [questionId]: next };
        });
    };

    // Repeatable-answer rows ('text'/'number'/'date' with Allow_Multiple_Answers)
    const handleAddRepeatableRow = (questionId: number) => {
        setAnswers((prev) => {
            const current = Array.isArray(prev[questionId]) ? (prev[questionId] as string[]) : [];
            return { ...prev, [questionId]: [...current, ''] };
        });
    };

    const handleRepeatableRowChange = (questionId: number, index: number, rowValue: string) => {
        setAnswers((prev) => {
            const current = Array.isArray(prev[questionId]) ? (prev[questionId] as string[]) : [];
            const next = current.map((v, i) => (i === index ? rowValue : v));
            return { ...prev, [questionId]: next };
        });
    };

    const handleRemoveRepeatableRow = (questionId: number, index: number) => {
        setAnswers((prev) => {
            const current = Array.isArray(prev[questionId]) ? (prev[questionId] as string[]) : [];
            return { ...prev, [questionId]: current.filter((_, i) => i !== index) };
        });
    };

    const handlePhotoSelect = async (
        questionId: number,
        eventTypeId: number,
        file: File | undefined,
        maxSizeMb?: number,
        appendToArray?: boolean,
    ) => {
        if (!file) return;
        if (maxSizeMb && file.size > maxSizeMb * 1024 * 1024) {
            setSnackbar({
                open: true,
                message: `File is larger than the ${maxSizeMb}MB limit for this question`,
                severity: 'warning',
            });
            return;
        }
        setUploadingQuestionId(questionId);
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('event_type_id', String(eventTypeId));
            if (normalizedOrgUUID) formData.append('organization_uuid', normalizedOrgUUID);

            const accessToken = SecureStorage.getAccessToken();
            const uploadUrl = `${import.meta.env.VITE_API_BASE_URL}${resolveRouterEndpoint(10673, 1)}/upload`;
            const response = await axios.post(uploadUrl, formData, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    'Content-Type': 'multipart/form-data',
                },
            });

            const blobUrl = response?.data?.data?.blob_url;
            if (blobUrl) {
                if (appendToArray) {
                    setAnswers((prev) => {
                        const current = Array.isArray(prev[questionId])
                            ? (prev[questionId] as string[])
                            : [];
                        return { ...prev, [questionId]: [...current, blobUrl] };
                    });
                } else {
                    setAnswer(questionId, blobUrl);
                }
                setSnackbar({ open: true, message: 'Photo uploaded', severity: 'success' });
            } else {
                throw new Error('Upload did not return a blob URL');
            }
        } catch (err: any) {
            setSnackbar({
                open: true,
                message: `Photo upload failed: ${err?.response?.data?.detail || err.message}`,
                severity: 'error',
            });
        } finally {
            setUploadingQuestionId(null);
        }
    };

    const handleSubmit = async () => {
        if (!selectedEventType) return;

        if (!normalizedOrgUUID) {
            setSnackbar({
                open: true,
                message:
                    'Please select a specific organization (not "All Organizations") before submitting.',
                severity: 'warning',
            });
            return;
        }

        const missingRequired = questions.filter((q) => {
            if (!q.isrequired) return false;
            const value = answers[q.submission_question_id];
            if (Array.isArray(value)) return value.length === 0;
            return value === '' || value === null || value === undefined;
        });
        if (missingRequired.length > 0) {
            setSnackbar({
                open: true,
                message: `Please answer all required questions: ${missingRequired
                    .map((q) => q.submission_question)
                    .join(', ')}`,
                severity: 'warning',
            });
            return;
        }

        setSubmitting(true);
        try {
            // Key the payload by question text (human-readable) rather than ID, since
            // the raw JSON is deliberately NOT parsed into DB columns in this build.
            const payload: Record<string, AnswerValue> = {};
            questions.forEach((q) => {
                payload[q.submission_question] = answers[q.submission_question_id];
            });

            const response = await apiRequestBySeq<ApiEnvelope<{ universal_event_uuid: string }[]>>(
                10673,
                1,
                {
                    manage: 2,
                    lifecycle_type_id: selectedLifecycleTypeId || null,
                    owner_organization_uuid: normalizedOrgUUID,
                    submission_pfuser_uuid: pfUser?.user_uuid || null,
                    source_system: 'event_submission_form',
                    eventtype_id: selectedEventType.event_type_id,
                    event_submission_payload: JSON.stringify(payload),
                },
            );

            const universalEventUuid = response?.data?.[0]?.universal_event_uuid || null;
            setSubmittedUuid(universalEventUuid);

            // Dedicated-mode questions ALSO get their answer dispatched to their
            // mapped, already-governed target controller (never raw table DML) via
            // the standalone Universal Answer Dispatcher router. This is a brand-new
            // Active_Universal_Event row every time this form is submitted, so every
            // dedicated dispatch here is always @manage=1 (Add) -- there is no
            // existing-submission edit flow in this component (see handleStartNew).
            if (universalEventUuid) {
                const dedicatedQuestions = questions.filter(
                    (q) =>
                        q.storage_mode === 'dedicated' &&
                        q.target_tsx_seq_num &&
                        q.target_field_name,
                );
                const dispatchFailures: string[] = [];
                for (const q of dedicatedQuestions) {
                    const rawValue = answers[q.submission_question_id];
                    const isEmpty =
                        rawValue === '' ||
                        rawValue === null ||
                        rawValue === undefined ||
                        (Array.isArray(rawValue) && rawValue.length === 0);
                    if (isEmpty) continue;

                    const answerValue = Array.isArray(rawValue)
                        ? JSON.stringify(rawValue)
                        : typeof rawValue === 'boolean'
                          ? rawValue
                              ? '1'
                              : '0'
                          : String(rawValue);

                    try {
                        await apiRequest('/api/universal-answer-dispatch', 'POST', {
                            manage: 1,
                            universal_event_uuid: universalEventUuid,
                            submission_question_id: q.submission_question_id,
                            answer_value: answerValue,
                            organization_uuid: q.target_pass_organization_uuid
                                ? normalizedOrgUUID
                                : null,
                            orgrole_id: q.target_pass_orgrole_id ? activeOrgRoleId : null,
                            pfuser_uuid: q.target_pass_pfuser_uuid ? pfUser?.user_uuid : null,
                        });
                    } catch (dispatchErr: any) {
                        console.error(
                            `Dispatch failed for question "${q.submission_question}":`,
                            dispatchErr,
                        );
                        dispatchFailures.push(q.submission_question);
                    }
                }

                if (dispatchFailures.length > 0) {
                    setSnackbar({
                        open: true,
                        message: `Submission received, but some answers could not be fully processed: ${dispatchFailures.join(', ')}`,
                        severity: 'warning',
                    });
                    setSubmitting(false);
                    return;
                }
            }

            setSnackbar({ open: true, message: 'Submission received', severity: 'success' });
        } catch (err: any) {
            setSnackbar({
                open: true,
                message: `Failed to submit: ${err?.response?.data?.detail || err.message}`,
                severity: 'error',
            });
        } finally {
            setSubmitting(false);
        }
    };

    const handleStartNew = () => {
        setSelectedEventType(null);
        setQuestions([]);
        setAnswers({});
        setSubmittedUuid(null);
    };

    // ════════════════════════════════════════
    // Render: Question Input Controls
    // ════════════════════════════════════════

    const renderQuestionInput = (q: SubmissionQuestion) => {
        const value = answers[q.submission_question_id];

        if (q.field_type === 'photo' || q.field_type === 'file') {
            const isPhoto = q.field_type === 'photo';
            const isUploading = uploadingQuestionId === q.submission_question_id;
            const isMulti = isRepeatableAnswer(q);
            // Admin-configured Accepted_File_Types drives the file picker's
            // accept filter; falls back to any image for 'photo' (camera-first,
            // unrestricted otherwise) or any file for the generic 'file' type.
            const acceptAttr = q.accepted_file_types || (isPhoto ? 'image/*' : undefined);
            const rows = isMulti && Array.isArray(value) ? (value as string[]) : [];
            const singleValue = !isMulti && typeof value === 'string' ? value : '';
            return (
                <Stack spacing={1}>
                    {isMulti &&
                        rows.map((rowValue, index) => (
                            <Stack
                                key={index}
                                direction="row"
                                spacing={1}
                                sx={{ alignItems: 'center' }}
                            >
                                {isPhoto ? (
                                    <Box
                                        component="img"
                                        src={rowValue}
                                        alt="Captured"
                                        sx={{
                                            maxWidth: 160,
                                            maxHeight: 120,
                                            borderRadius: 1,
                                            objectFit: 'cover',
                                        }}
                                    />
                                ) : (
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        <a href={rowValue} target="_blank" rel="noreferrer">
                                            File {index + 1}
                                        </a>
                                    </Typography>
                                )}
                                <IconButton
                                    size="small"
                                    onClick={() =>
                                        handleRemoveRepeatableRow(q.submission_question_id, index)
                                    }
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Stack>
                        ))}
                    <Button
                        variant="outlined"
                        component="label"
                        startIcon={
                            isUploading ? (
                                <CircularProgress size={16} />
                            ) : isPhoto ? (
                                <PhotoCameraIcon />
                            ) : (
                                <AttachFileIcon />
                            )
                        }
                        disabled={isUploading}
                        sx={{ alignSelf: 'flex-start' }}
                    >
                        {isMulti
                            ? isPhoto
                                ? 'Take / Upload Another Photo'
                                : 'Upload Another File'
                            : singleValue
                              ? isPhoto
                                  ? 'Retake Photo'
                                  : 'Replace File'
                              : isPhoto
                                ? 'Take / Upload Photo'
                                : 'Upload File'}
                        <input
                            type="file"
                            accept={acceptAttr}
                            capture={isPhoto ? 'environment' : undefined}
                            hidden
                            onChange={(e) =>
                                handlePhotoSelect(
                                    q.submission_question_id,
                                    q.event_type_id,
                                    e.target.files?.[0],
                                    q.max_file_size_mb,
                                    isMulti,
                                )
                            }
                        />
                    </Button>
                    {isPhoto && singleValue && (
                        <Box
                            component="img"
                            src={singleValue}
                            alt="Captured"
                            sx={{
                                maxWidth: 240,
                                maxHeight: 180,
                                borderRadius: 1,
                                objectFit: 'cover',
                            }}
                        />
                    )}
                    {!isPhoto && singleValue && (
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            File uploaded:{' '}
                            <a href={singleValue} target="_blank" rel="noreferrer">
                                view
                            </a>
                        </Typography>
                    )}
                </Stack>
            );
        }

        if (isRepeatableAnswer(q)) {
            const rows = Array.isArray(value) ? (value as string[]) : [];
            const inputType =
                q.field_type === 'number' ? 'number' : q.field_type === 'date' ? 'date' : 'text';
            return (
                <Stack spacing={1}>
                    {rows.map((rowValue, index) => (
                        <Stack
                            key={index}
                            direction="row"
                            spacing={1}
                            sx={{ alignItems: 'center' }}
                        >
                            <TextField
                                type={inputType}
                                fullWidth
                                size="small"
                                value={rowValue}
                                onChange={(e) =>
                                    handleRepeatableRowChange(
                                        q.submission_question_id,
                                        index,
                                        e.target.value,
                                    )
                                }
                                slotProps={
                                    inputType === 'date'
                                        ? { inputLabel: { shrink: true } }
                                        : undefined
                                }
                            />
                            <IconButton
                                size="small"
                                onClick={() =>
                                    handleRemoveRepeatableRow(q.submission_question_id, index)
                                }
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Stack>
                    ))}
                    <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => handleAddRepeatableRow(q.submission_question_id)}
                        sx={{ alignSelf: 'flex-start' }}
                    >
                        Add Another Answer
                    </Button>
                </Stack>
            );
        }

        if (q.field_type === 'boolean') {
            return (
                <FormControlLabel
                    control={
                        <Switch
                            checked={!!value}
                            onChange={(e) => setAnswer(q.submission_question_id, e.target.checked)}
                        />
                    }
                    label={value ? 'Yes' : 'No'}
                />
            );
        }

        // FK Lookup questions reuse the single/multi choice rendering -- their
        // selectable options were seeded (and can be re-synced) from a live DB
        // table's rows into Options_Json at authoring time, so no separate
        // runtime FK lookup call is needed here.
        const isSingleChoiceLike =
            q.field_type === 'single_choice' ||
            (q.field_type === 'fk_lookup' && !q.fk_allow_multiple);
        const isMultiChoiceLike =
            q.field_type === 'multi_choice' ||
            (q.field_type === 'fk_lookup' && !!q.fk_allow_multiple);

        if (isSingleChoiceLike) {
            let options: QuestionOption[] = [];
            try {
                options = q.options_json ? JSON.parse(q.options_json) : [];
            } catch {
                options = [];
            }
            return (
                <RadioGroup
                    value={value || ''}
                    onChange={(e) => setAnswer(q.submission_question_id, e.target.value)}
                >
                    {options.map((opt) => (
                        <FormControlLabel
                            key={opt.option_id}
                            value={opt.option_value}
                            control={<Radio size="small" />}
                            label={opt.option_label}
                        />
                    ))}
                </RadioGroup>
            );
        }

        if (isMultiChoiceLike) {
            let options: QuestionOption[] = [];
            try {
                options = q.options_json ? JSON.parse(q.options_json) : [];
            } catch {
                options = [];
            }
            const selected = Array.isArray(value) ? value : [];
            return (
                <FormGroup>
                    {options.map((opt) => (
                        <FormControlLabel
                            key={opt.option_id}
                            control={
                                <Checkbox
                                    size="small"
                                    checked={selected.includes(opt.option_value)}
                                    onChange={(e) =>
                                        handleMultiChoiceToggle(
                                            q.submission_question_id,
                                            opt.option_value,
                                            e.target.checked,
                                        )
                                    }
                                />
                            }
                            label={opt.option_label}
                        />
                    ))}
                </FormGroup>
            );
        }

        if (q.field_type === 'date') {
            return (
                <TextField
                    type="date"
                    fullWidth
                    size="small"
                    value={value || ''}
                    onChange={(e) => setAnswer(q.submission_question_id, e.target.value)}
                    slotProps={{ inputLabel: { shrink: true } }}
                />
            );
        }

        if (q.field_type === 'number') {
            return (
                <TextField
                    type="number"
                    fullWidth
                    size="small"
                    value={value ?? ''}
                    onChange={(e) =>
                        setAnswer(
                            q.submission_question_id,
                            e.target.value === '' ? '' : Number(e.target.value),
                        )
                    }
                />
            );
        }

        // Default: text
        return (
            <TextField
                fullWidth
                size="small"
                value={value || ''}
                onChange={(e) => setAnswer(q.submission_question_id, e.target.value)}
                slotProps={{ htmlInput: { maxLength: 500 } }}
            />
        );
    };

    // ════════════════════════════════════════
    // Render
    // ════════════════════════════════════════

    const livePreview = questions.reduce<Record<string, AnswerValue>>((acc, q) => {
        acc[q.submission_question] = answers[q.submission_question_id];
        return acc;
    }, {});

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                p: 2,
                maxWidth: 900,
                mx: 'auto',
            }}
        >
            {error && <Alert severity="error">{error}</Alert>}

            <Typography variant="h5">Submit an Event</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Pick the lifecycle type and event type, then answer the questions below.
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <FormControl sx={{ flex: 1 }} disabled={!isAllOrgsActive}>
                    <InputLabel id="org-select-label">Organization</InputLabel>
                    <Select
                        labelId="org-select-label"
                        label="Organization"
                        value={isAllOrgsActive ? selectedOrgUuid : activeOrganizationUUID || ''}
                        onChange={handleOrgChange}
                    >
                        {isAllOrgsActive ? (
                            userOrganizations.map((org) => (
                                <MenuItem key={org.organization_uuid} value={org.organization_uuid}>
                                    {org.organization_name}
                                </MenuItem>
                            ))
                        ) : (
                            <MenuItem value={activeOrganizationUUID || ''}>
                                {userOrganizations.find(
                                    (org) => org.organization_uuid === activeOrganizationUUID,
                                )?.organization_name || 'Current Organization'}
                            </MenuItem>
                        )}
                    </Select>
                    <FormHelperText>
                        {isAllOrgsActive
                            ? 'Select which organization this submission belongs to'
                            : 'Preset from your current organization filter'}
                    </FormHelperText>
                </FormControl>

                <FormControl sx={{ flex: 1 }} disabled={initialLifecycleTypeId !== undefined}>
                    <InputLabel id="lifecycle-type-select-label">Lifecycle Type</InputLabel>
                    <Select
                        labelId="lifecycle-type-select-label"
                        label="Lifecycle Type"
                        value={selectedLifecycleTypeId}
                        onChange={handleLifecycleTypeChange}
                    >
                        {lifecycleTypes.map((t) => (
                            <MenuItem key={t.lifecycle_type_id} value={t.lifecycle_type_id}>
                                {t.type_name} ({t.type_code})
                            </MenuItem>
                        ))}
                    </Select>
                    {initialLifecycleTypeId !== undefined && (
                        <FormHelperText>Preset for this quick action</FormHelperText>
                    )}
                </FormControl>

                <FormControl sx={{ flex: 1 }} disabled={selectedLifecycleTypeId === ''}>
                    <InputLabel id="event-type-select-label">Event Type</InputLabel>
                    <Select
                        labelId="event-type-select-label"
                        label="Event Type"
                        value={selectedEventType?.event_type_id ?? ''}
                        onChange={handleEventTypeChange}
                    >
                        {eventTypes.map((et) => (
                            <MenuItem key={et.event_type_id} value={et.event_type_id}>
                                {et.event_type}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Stack>

            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress size={24} />
                </Box>
            )}

            {submittedUuid ? (
                <Card sx={{ mt: 2 }}>
                    <CardContent sx={{ textAlign: 'center' }}>
                        <CheckCircleIcon color="success" sx={{ fontSize: 56, mb: 1 }} />
                        <Typography variant="h6">Submission Received</Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                            Confirmation: {submittedUuid}
                        </Typography>
                        <Button variant="contained" onClick={handleStartNew}>
                            Submit Another Event
                        </Button>
                    </CardContent>
                </Card>
            ) : (
                selectedEventType && (
                    <Card sx={{ mt: 1 }}>
                        <CardContent>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                {selectedEventType.event_type}
                            </Typography>

                            {questionsLoading && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                                    <CircularProgress size={24} />
                                </Box>
                            )}

                            {!questionsLoading && questions.length === 0 && (
                                <Typography sx={{ color: 'text.secondary' }}>
                                    No submission questions are configured for this event type yet.
                                </Typography>
                            )}

                            <Stack spacing={3} sx={{ mt: 1 }}>
                                {questions.map((q) => (
                                    <Box key={q.submission_question_id}>
                                        <Stack
                                            direction="row"
                                            spacing={1}
                                            sx={{ alignItems: 'center', mb: 0.5 }}
                                        >
                                            <Typography variant="subtitle2">
                                                {q.submission_question}
                                            </Typography>
                                            {!!q.isrequired && (
                                                <Chip
                                                    label="Required"
                                                    size="small"
                                                    color="primary"
                                                    variant="outlined"
                                                />
                                            )}
                                        </Stack>
                                        {renderQuestionInput(q)}
                                    </Box>
                                ))}
                            </Stack>

                            {questions.length > 0 && (
                                <>
                                    <Box
                                        sx={{
                                            mt: 3,
                                            p: 1.5,
                                            bgcolor: 'action.hover',
                                            borderRadius: 1,
                                            fontFamily: 'monospace',
                                            fontSize: 12,
                                            whiteSpace: 'pre-wrap',
                                            maxHeight: 200,
                                            overflow: 'auto',
                                        }}
                                    >
                                        {JSON.stringify(livePreview, null, 2)}
                                    </Box>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        sx={{ mt: 2 }}
                                        disabled={submitting}
                                        onClick={handleSubmit}
                                    >
                                        {submitting ? <CircularProgress size={20} /> : 'Submit'}
                                    </Button>
                                </>
                            )}
                        </CardContent>
                    </Card>
                )
            )}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert
                    severity={snackbar.severity}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

Self.displayName = getTsxFileName(import.meta.url);
export default Self;
